import { EventDispatcher } from './eventSystem/EventDispatcher';
import { getIndexedDbPersister } from './indexedDb/indexedDbPersister';
import { getInMemoryPersister } from './inMemory/InMemoryPersister';
import { getLocalSessionPersister } from './localStorage/LocalSessionPersister';

/**
 * Persisters available are:
 * - IndexedDB - Uses indexedDB
 * - LocalStorage - Uses LocalStorage
 * - inMemory - uses JS Map object for in memory storage
 */

const SETTINGS_NAME = 'serviceSettings';

class GlobalAppService {
  constructor() {
    this.settingsStoreConfig = { name: SETTINGS_NAME, keyPath: 'key', type: 'local' };
    this.eventDispatcher = new EventDispatcher();
    this.serviceSettings = {
      showDebugMessages: false,
    };

    // This Map will hold the unresolved promises tied to a web service call.
    this.webServiceCalls = new Map();
    this.inMemStorage = null;
    this.cachePersister = null;
    this.services = null;
    this.stores = null;
  }

  static pageDidReload() {
    try {
      return window.performance.getEntriesByType('navigation')[0].type === 'reload';
    } catch (err) {
      try {
        // Need to try deprecated value for iOS 14 safari. This can be removed when stop supporting that browser
        // https://bugs.webkit.org/show_bug.cgi?id=184363
        return performance.navigation.type === performance.navigation.TYPE_RELOAD;
      } catch (totalFail) {
        console.warn('Unable to detect page reload using modern and deprecated techniques', err, totalFail);
        return false;
      }
    }
  }

  clearPersister() {
    return new Promise((resolve, reject) => {
      try {
        this.cachePersister.kill();
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });
  }

  clearAllStores() {
    Object.values(this.stores).forEach((s) => s.clear());
    Object.values(this.cachePersister.stores).forEach((s) => s.clear());
  }

  getServices() {
    return this.services;
  }

  listServices() {
    return Object.keys(this.getServices());
  }

  async showDebugMessages(shouldDisplay) {
    const newSettings = { ...this.serviceSettings, showDebugMessages: shouldDisplay };
    const ttl = Date.now() + 99999 * 1000;
    await this.stores.serviceSettings.upsert(SETTINGS_NAME, newSettings, ttl);
    await this.cachePersister.stores.serviceSettings.upsert(SETTINGS_NAME, newSettings, ttl);
    const { data } = await this.stores.serviceSettings.get(SETTINGS_NAME);
    this.serviceSettings = data;
    this.inMemStorage.setDebug(shouldDisplay);
    this.cachePersister.setDebug(shouldDisplay);
  }

  debugMessage(...args) {
    if (this.serviceSettings.showDebugMessages || window.showAtsDebugMessage === true) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  }

  /**
   * Generates event names.
   * @param {string} key Usually the name of the service
   * @returns A lookup of event names
   */
  static generateEventNames(key) {
    return {
      loading: `AGS_${key}_loading`,
      success: `AGS_${key}_success`,
      failure: `AGS_${key}_failure`,
      refresh: `AGS_${key}_refresh'`,
    };
  }

  /**
   * Generates a key with a given prefix and list of additional parameters
   * @param {string} prefix
   * @param  {...any} params
   * @returns string
   */
  static makeKeyFromParams(prefix, ...params) {
    return prefix + JSON.stringify(params);
  }

  clearWebServicePromise(funcKey) {
    if (this.webServiceCalls.has(funcKey)) {
      this.webServiceCalls.delete(funcKey);
    }
  }

  /**
   * Batch redundant calls and return the same promise while request is in progress.
   * This needs to be done at call time because function parameters are needed to
   * identify redundant function calls.
   *
   * @param {Object} serviceConfig as defined in the serviceConfigs
   * @param {function} serviceFunc The service call
   * @param  {...any} params Any parameters used to make the call
   * @returns Promise<any>
   */
  batchedCallHandler(serviceConfig, serviceFunc, ...params) {
    const { key, events } = serviceConfig;
    // check for existing unresolved call
    const funcKey = GlobalAppService.makeKeyFromParams(key, ...params);
    const { callPromise } = this.webServiceCalls.get(funcKey) || {};
    if (callPromise) {
      return callPromise;
    }
    // make the call and put the promise in the webServiceCalls map.
    const originalPromise = serviceFunc(...params);
    // Fire an event for loading started
    this.eventDispatcher.dispatch(events.loading, { funcKey });

    // Async functions always return a promise so we can use async here to wrap a
    // promise around the webservice call's promise for webServiceCalls clean up.
    // When the service call resolves remove the promise from the lookup.
    const promiseWrapper = async () => {
      try {
        const result = await originalPromise;
        // Fire an event for loading successful.
        this.eventDispatcher.dispatch(events.success, { funcKey, result });
        this.clearWebServicePromise(funcKey);
        return result;
      } catch (err) {
        console.error(err);
        // Fire an event for loading failed.
        this.eventDispatcher.dispatch(events.failure, { funcKey, err });
        this.clearWebServicePromise(funcKey);
        throw err;
      }
    };
    const wrappedPromise = promiseWrapper();
    this.webServiceCalls.set(funcKey, { callPromise: wrappedPromise });
    return wrappedPromise;
  }

  /**
   * This is a curried function to help bind a service call to storage. Used for convenience
   * because much of the set, get, and delete logic is redundant across call handlers.
   *
   * @param {Object} serviceConfig as defined in the serviceConfigs
   * @returns function
   */
  bindCallToStorage(serviceConfig) {
    const { serviceCall, key, ttl, store, events } = serviceConfig;
    const cacheStore = this.cachePersister?.stores[store.name] || undefined;
    return async (...params) => {
      const storageKey = GlobalAppService.makeKeyFromParams(key, ...params);
      let existingResult = await store.get(storageKey);
      let foundInCache = false;
      if (cacheStore && ttl > 0 && existingResult === undefined) {
        try {
          existingResult = await cacheStore.get(storageKey);
          foundInCache = true;
        } catch (err) {
          console.warn('Cache store is broken. Implement some reporting code in here.');
          console.error(err);
          // Database is locked for some reason, Mark false and continue on. Should correct itself on the next page
          foundInCache = false;
        }
      }
      const isExpired = Date.now() > existingResult?.expiresAt || 0;
      if (isExpired) {
        this.debugMessage(`🔥 Expired Kill ${storageKey} with 🔥`);
        // TODO Do we need to trigger delete events?
        if (cacheStore) {
          await cacheStore.delete(storageKey);
        }
        await store.delete(storageKey);
      }

      if (existingResult !== undefined && !isExpired) {
        this.debugMessage(`💾 Found ${storageKey} from storage 💾`);
        if (foundInCache) {
          // Put this back in memory storage
          store.set(storageKey, existingResult.data, Date.now() + ttl);
        }
        return existingResult.data;
      }
      this.debugMessage(`🤙 ${storageKey} with params`, params);
      const result = await serviceCall(...params);
      try {
        store.set(storageKey, result, Date.now() + ttl);
        if (cacheStore && ttl > 0) {
          cacheStore.set(storageKey, result, Date.now() + ttl);
        }
        this.eventDispatcher.dispatch(events.refresh, { storageKey });
      } catch (err) {
        console.error(err);
        throw err;
      }
      return result;
    };
  }

  makeFetchObject(serviceConfig, serviceCall) {
    const { key, store, events } = serviceConfig;
    const fetchObject = {
      fetch: (...params) => this.batchedCallHandler(serviceConfig, serviceCall, ...params),
      keyFromParams: (...params) => GlobalAppService.makeKeyFromParams(key, ...params),
      // clear out data from in memory store and cache store
      invalidateKey: async (...params) => {
        const resolvedKey = params ? GlobalAppService.makeKeyFromParams(key, ...params) : key;
        const cacheStore = this.cachePersister?.stores[store.name] || undefined;
        let existingResult = await store.get(resolvedKey);
        if (existingResult === undefined) {
          existingResult = await cacheStore.get(resolvedKey);
        }
        if (existingResult !== undefined) {
          this.debugMessage(`💥 ${resolvedKey} invalidating key 💥`);
          if (cacheStore) {
            await cacheStore.delete(resolvedKey);
          }
          await store.delete(resolvedKey);
          this.eventDispatcher.dispatch(events.refresh);
        }
      },
      invalidateMemoryStore: async (...params) => {
        const resolvedKey = params ? GlobalAppService.makeKeyFromParams(key, ...params) : key;
        const existingResult = await store.get(resolvedKey);
        if (existingResult !== undefined) {
          this.debugMessage(`💥 ${resolvedKey} invalidating memory store only 💥`);
          await store.delete(resolvedKey);
          this.eventDispatcher.dispatch(events.refresh);
        }
      },
      events,
      // expose event on and off methods. Need to wrap them in function to avoid what I think is a stale closure
      on: (...params) => this.eventDispatcher.on(...params),
      off: (...params) => this.eventDispatcher.off(...params),
    };

    fetchObject.fetchAndReplace = async (...params) => {
      await fetchObject.invalidateKey(...params);
      return fetchObject.fetch(...params);
    };

    fetchObject.clearServiceData = async () => {
      const cacheStore = this.cachePersister?.stores[store.name] || undefined;
      if (cacheStore) {
        cacheStore.deleteStartsWith(key);
      }
      store.deleteStartsWith(key);
    };
    return fetchObject;
  }

  generateService(serviceConfig) {
    const serviceCallHandler = this.bindCallToStorage(serviceConfig);
    return this.makeFetchObject(serviceConfig, serviceCallHandler);
  }

  /**
   * Initializes the GlobalAppService. Cannot do this in the constructor because it is asynchronous.
   * @param {Object} config Config options. TODO write these option out.
   * @returns Promise<Any>
   */
  async init(config) {
    const { name, storeIdentifiers, serviceConfigs } = config;
    if (window.showAtsDebugMessage) {
      // eslint-disable-next-line no-console
      console.log('🏗️ initGlobalAppService making instance.', config);
    }
    const { settingsStoreConfig } = this;
    const allStoreConfigs = { settingsStoreConfig, ...storeIdentifiers };

    // build a cache persister here.
    const persisterBuilder = GlobalAppService.determinePersister();
    // Generate the stores and associated methods
    this.cachePersister = await persisterBuilder(name, allStoreConfigs);
    this.inMemStorage = await getInMemoryPersister(name, allStoreConfigs);

    // restore any config settings from the storage. Don't block on this.
    this.cachePersister.stores.serviceSettings.get(SETTINGS_NAME).then((res) => {
      if (res?.data) {
        this.serviceSettings = res.data;
        this.inMemStorage.setDebug(res.data?.showDebugMessages);
        this.cachePersister.setDebug(res.data?.showDebugMessages);
      }
    });

    // bind the store to the serviceConfig
    Object.entries(serviceConfigs).forEach(([key, sc]) => {
      // apply defaults
      const resolvedConfig = {
        ...sc,
        ttl: sc.ttl !== undefined ? sc.ttl : 3_600_000,
        events: GlobalAppService.generateEventNames(sc.key),
        store: this.inMemStorage.stores[sc.storeConfig.name],
        clearOnRefresh: Boolean(sc?.clearOnRefresh),
      };
      serviceConfigs[key] = resolvedConfig;
    });
    // Loop through each serviceConfig and generate a service.
    this.services = Object.values(serviceConfigs).reduce((acc, service) => {
      return { ...acc, [service.key]: this.generateService(service) };
    }, {});

    if (GlobalAppService.pageDidReload()) {
      if (window.showAtsDebugMessage) {
        // eslint-disable-next-line no-console
        console.log('🔄 Reload detected. Clearing out services marked by `clearOnRefresh`');
      }
      Object.values(serviceConfigs)
        .filter((sc) => sc.clearOnRefresh)
        .forEach((sc) => {
          this.cachePersister.stores[sc.storeConfig.name].deleteStartsWith(sc.key);
        });
    }

    this.stores = this.inMemStorage.stores;
    return this;
  }

  static localStorageAvailable() {
    const storage = window.localStorage;
    try {
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  }

  static determinePersister() {
    if (window?.indexedDB) {
      return getIndexedDbPersister;
    }
    if (GlobalAppService.localStorageAvailable()) {
      return getLocalSessionPersister;
    }
    return null;
  }
}

export default GlobalAppService;
